// public/js/features/files/explorer-page.js
// The tenant-wide files explorer (#files) — docs/asset-files.md §5/§7.
//
// One place to find any document in the tenant, no matter which asset it hangs
// on: keyword search over labels/filenames/tags/extracted text (the server
// scores it), an "Ask AI" mode that answers a question over the indexed texts
// and links the sources, plus the plain filters people actually use (doc type,
// asset, indexing state, date range) and a list ⇄ tiles toggle.
//
// Tenant scope: files are tenant-partitioned at the storage layer, and the
// server refuses a cross-tenant listing, so the explorer is only meaningful
// with ONE active tenant. Under "All tenants" it prompts for a scope instead of
// silently showing a subset — same contract as the tenant-bound settings tabs.
//
// Row/tile markup, the viewer wiring and the lazy thumbnails come from
// asset-files-section.js, so a file looks and behaves identically on both
// surfaces.
// `searchTenantFiles` is the positional (tenantId, q, { ask }) form of the
// api layer's object-argument `searchFiles` — same call, friendlier shape.
import { listTenantFiles, listDocTypes, searchTenantFiles as searchFiles } from './api.js';
import { openUploader } from './uploader.js';
import {
  fileRowHtml, fileTileHtml, mountThumbs, wireItemActions, docTypeLabel,
} from './asset-files-section.js';
import { openFileViewer } from './viewer-modal.js';
import { injectFilesCss } from './explorer.css.js';
import { escapeHtml } from '../../utils/escape.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { scopeTenantParam, isAllTenants } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';
import { init as initAssets, sync as syncAssets, getCached as cachedAssets } from '../assets/asset-store.js';

const VIEW_KEY = 'fx.explorerView';        // 'list' | 'tiles'
const SEARCH_DEBOUNCE_MS = 280;

const readView = () => (localStorage.getItem(VIEW_KEY) === 'tiles' ? 'tiles' : 'list');
const writeView = (v) => { try { localStorage.setItem(VIEW_KEY, v); } catch (e) {} };

/** Display name for an asset row; the same fallback chain the assets list uses. */
function assetTitle(a) {
  return a?.label || a?.reference || a?.assetId || a?.assetUUID || 'Unknown asset';
}

export async function initFilesPage(container) {
  injectFilesCss();

  const groups = getCurrentUser()?.groups || [];
  const canManage = can(groups, 'manageAssetFiles');
  const canDelete = can(groups, 'deleteAssetFiles');

  const tenantId = scopeTenantParam();
  if (isAllTenants() || !tenantId || tenantId === 'unassigned') {
    container.innerHTML = `<div class="container-fluid py-3">
      <h1 class="h4 mb-3"><i class="fa-solid fa-folder-open me-2"></i>Files</h1>
      ${scopePromptHtml('the files explorer')}
    </div>`;
    return;
  }

  container.innerHTML = shellHtml();
  if (!canManage) container.querySelector('#fx-upload')?.remove();

  // ── State ────────────────────────────────────────────────────────────────
  // `ctx` is handed to the shared action delegate and stays alive for the life
  // of the page — the delegate reads ctx.files at click time (see
  // wireItemActions), so redrawing the body never re-attaches handlers.
  const ctx = {
    files: [],            // the rows currently RENDERED (post filter/search)
    docTypes: [],
    canManage, canDelete,
    onChanged: () => reload(),
    assetNameOf: (f) => assetNames.get(f.assetUUID) || '',
  };
  let allFiles = [];       // everything the tenant has (the unfiltered listing)
  let searchHits = [];     // server-scored hits for the current query
  let answer = null;       // last "Ask AI" answer, or null
  let searching = false;
  let detachThumbs = () => {};
  const assetNames = new Map();
  const filters = { q: '', docTypeId: '', assetUUID: '', kind: '', ai: '', from: '', to: '' };

  const $ = (sel) => container.querySelector(sel);

  wireItemActions(container, ctx);

  // ── Data ─────────────────────────────────────────────────────────────────
  async function loadAssetNames() {
    // The asset store is already tenant-scope aware and usually warm from the
    // map/list; a failed sync only costs nicer labels.
    try {
      await initAssets();
      if (!cachedAssets().length) await syncAssets();
      for (const a of cachedAssets()) assetNames.set(a.assetUUID, assetTitle(a));
    } catch (e) { console.warn('[files] asset names unavailable', e?.message); }
  }

  async function reload() {
    try {
      allFiles = (await listTenantFiles(tenantId, {})) || [];
    } catch (e) {
      console.warn('[files] tenant listing failed', e?.message);
      const body = $('#fx-body');
      if (body) body.innerHTML = '<div class="alert alert-danger">Failed to load files.</div>';
      return;
    }
    if (filters.q) await runSearch({ ask: false });
    else { answer = null; draw(); }
  }

  // Keyword search runs server-side (it also covers the extracted text, which
  // the browser never receives). An empty query falls back to the full listing.
  let searchSeq = 0;
  async function runSearch({ ask }) {
    const q = filters.q.trim();
    if (!q) { answer = null; draw(); return; }
    const seq = ++searchSeq;
    searching = true;
    draw();
    try {
      const res = await searchFiles(tenantId, q, { ask }) || {};
      if (seq !== searchSeq) return;              // a newer keystroke won
      searchHits = res.results || [];
      if (ask) answer = res.answer || { answer: 'No answer could be generated from the indexed documents.', usedFileIds: [] };
    } catch (e) {
      if (seq !== searchSeq) return;
      searchHits = [];
      window.showToast?.(`Search failed: ${e.message}`, 'danger');
    } finally {
      if (seq === searchSeq) { searching = false; draw(); }
    }
  }

  // ── Filtering ────────────────────────────────────────────────────────────
  function visibleFiles() {
    const base = filters.q.trim() ? searchHits : allFiles;
    const fromMs = filters.from ? Date.parse(`${filters.from}T00:00:00`) : null;
    const toMs = filters.to ? Date.parse(`${filters.to}T23:59:59`) : null;

    const out = base.filter(f => {
      if (filters.kind && (f.itemType || 'file') !== filters.kind) return false;
      if (filters.docTypeId && f.docTypeId !== filters.docTypeId) return false;
      if (filters.assetUUID && f.assetUUID !== filters.assetUUID) return false;
      if (filters.ai === 'indexed' && f.ai?.status !== 'indexed') return false;
      if (filters.ai === 'none' && f.ai?.status === 'indexed') return false;
      if (fromMs || toMs) {
        const t = Date.parse(f.uploadedAt || '');
        if (!Number.isFinite(t)) return false;
        if (fromMs && t < fromMs) return false;
        if (toMs && t > toMs) return false;
      }
      return true;
    });

    // Search results keep the server's relevance order; a plain listing is
    // newest-first, which is what people expect from a document store.
    if (!filters.q.trim()) out.sort((a, b) => String(b.uploadedAt || '').localeCompare(String(a.uploadedAt || '')));
    return out;
  }

  const filtersActive = () =>
    !!(filters.q.trim() || filters.docTypeId || filters.assetUUID || filters.kind || filters.ai || filters.from || filters.to);

  // ── Rendering ────────────────────────────────────────────────────────────
  function draw() {
    // The router can swap the page out mid-request; drawing into a detached
    // shell would throw on the first missing node.
    const body = $('#fx-body');
    if (!body) return;
    detachThumbs();
    ctx.files = visibleFiles();
    drawFilterOptions();
    $('#fx-answer').innerHTML = answerHtml();
    $('#fx-count').textContent = searching
      ? 'Searching…'
      : `${ctx.files.length} of ${allFiles.length} file${allFiles.length === 1 ? '' : 's'}`;

    const view = readView();
    container.querySelectorAll('[data-view]').forEach(b =>
      b.className = `btn ${b.dataset.view === view ? 'btn-primary' : 'btn-outline-secondary'}`);

    if (searching && !ctx.files.length) {
      body.innerHTML = '<div class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Searching…</div>';
      return;
    }
    if (!ctx.files.length) {
      body.innerHTML = allFiles.length || filtersActive() ? noMatchHtml() : emptyHtml();
      body.querySelector('[data-role="clear"]')?.addEventListener('click', clearFilters);
      body.querySelector('[data-role="upload"]')?.addEventListener('click', startUpload);
      return;
    }

    const sub = (f) => `<span class="text-truncate" style="max-width:180px;">${escapeHtml(assetNames.get(f.assetUUID) || 'Unknown asset')}</span>`
      + `<span class="badge badge-soft badge-soft-gray">${escapeHtml(f.itemType === 'photo' ? 'Photo' : docTypeLabel(ctx.docTypes, f.docTypeId))}</span>`;

    body.innerHTML = view === 'tiles'
      ? `<div class="fx-tiles">${ctx.files.map(f => fileTileHtml(f, { ...ctx, extraSub: sub(f) })).join('')}</div>`
      : `<div class="card shadow-sm">${ctx.files.map(f => fileRowHtml(f, { ...ctx, extraSub: sub(f) })).join('')}</div>`;
    detachThumbs = mountThumbs(body);
  }

  function answerHtml() {
    if (!answer) return '';
    const text = typeof answer === 'string' ? answer : (answer.answer || '');
    const ids = (typeof answer === 'object' && Array.isArray(answer.usedFileIds)) ? answer.usedFileIds : [];
    const byId = new Map([...allFiles, ...searchHits].map(f => [f.fileId, f]));
    const sources = ids.map(id => byId.get(id)).filter(Boolean);
    return `
      <div class="fx-answer mb-3">
        <div class="d-flex align-items-center gap-2 mb-2">
          <i class="fa-solid fa-wand-magic-sparkles text-primary"></i>
          <span class="fw-semibold small text-primary">Answer from your documents</span>
          <button type="button" class="btn btn-sm btn-link ms-auto p-0 text-secondary" data-role="answer-close">Dismiss</button>
        </div>
        <div class="fx-answer-text">${escapeHtml(text)}</div>
        ${sources.length ? `
          <div class="fx-answer-sources">
            <span class="small text-muted me-1">Sources:</span>
            ${sources.map(f => `<button type="button" class="btn btn-sm btn-outline-primary" data-src-file="${escapeHtml(f.fileId)}">
              <i class="fa-solid fa-file-lines me-1"></i>${escapeHtml(f.label || f.fileName || 'File')}</button>`).join('')}
          </div>` : `<div class="small text-muted mt-2">The answer is drawn from the top matches — refine the question for tighter sources.</div>`}
      </div>`;
  }

  function emptyHtml() {
    return `
      <div class="card shadow-sm"><div class="fx-empty">
        <i class="fa-regular fa-folder-open"></i>
        <div class="fw-semibold text-body">No files yet — upload the first one</div>
        <div class="small">Certificates, release forms, inspection reports, manuals: anything you attach to an asset shows up here, searchable by its content.</div>
        ${canManage ? '<button type="button" class="btn btn-sm btn-primary mt-3" data-role="upload"><i class="fa-solid fa-upload me-1"></i>Upload a file</button>' : ''}
      </div></div>`;
  }

  function noMatchHtml() {
    return `
      <div class="card shadow-sm"><div class="fx-empty">
        <i class="fa-solid fa-magnifying-glass"></i>
        <div class="fw-semibold text-body">Nothing matches</div>
        <div class="small">No file in this tenant matches the current search and filters.</div>
        <button type="button" class="btn btn-sm btn-outline-secondary mt-3" data-role="clear"><i class="fa-solid fa-eraser me-1"></i>Clear search &amp; filters</button>
      </div></div>`;
  }

  // Option lists are derived from the files that actually exist, so the selects
  // never offer a filter that can only ever return nothing.
  function drawFilterOptions() {
    const typeSel = $('#fx-f-type');
    const assetSel = $('#fx-f-asset');

    const usedTypes = [...new Set(allFiles.filter(f => f.itemType !== 'photo').map(f => f.docTypeId || 'other'))]
      .sort((a, b) => String(docTypeLabel(ctx.docTypes, a)).localeCompare(String(docTypeLabel(ctx.docTypes, b))));
    const typeOpts = `<option value="">All document types</option>`
      + usedTypes.map(id => `<option value="${escapeHtml(id)}" ${filters.docTypeId === id ? 'selected' : ''}>${escapeHtml(docTypeLabel(ctx.docTypes, id))}</option>`).join('');
    if (typeSel.dataset.sig !== typeOpts) { typeSel.dataset.sig = typeOpts; typeSel.innerHTML = typeOpts; }

    const usedAssets = [...new Set(allFiles.map(f => f.assetUUID))]
      .sort((a, b) => String(assetNames.get(a) || a).localeCompare(String(assetNames.get(b) || b)));
    const assetOpts = `<option value="">All assets</option>`
      + usedAssets.map(id => `<option value="${escapeHtml(id)}" ${filters.assetUUID === id ? 'selected' : ''}>${escapeHtml(assetNames.get(id) || id)}</option>`).join('');
    if (assetSel.dataset.sig !== assetOpts) { assetSel.dataset.sig = assetOpts; assetSel.innerHTML = assetOpts; }
  }

  function clearFilters() {
    Object.assign(filters, { q: '', docTypeId: '', assetUUID: '', kind: '', ai: '', from: '', to: '' });
    answer = null;
    searchHits = [];
    $('#fx-q').value = '';
    ['#fx-f-type', '#fx-f-asset', '#fx-f-kind', '#fx-f-ai', '#fx-f-from', '#fx-f-to'].forEach(s => { const el = $(s); if (el) el.value = ''; });
    draw();
  }

  // ── Upload (needs an asset — the explorer is not asset-bound) ─────────────
  async function startUpload() {
    const assets = cachedAssets();
    if (!assets.length) { window.showToast?.('No assets available to attach a file to', 'info'); return; }
    const assetUUID = await pickAsset(assets);
    if (!assetUUID) return;
    openUploader({ assetUUID, kind: 'file', docTypes: ctx.docTypes, onDone: () => reload() });
  }

  // ── Wiring ───────────────────────────────────────────────────────────────
  let debounce = null;
  $('#fx-q').addEventListener('input', (e) => {
    filters.q = e.target.value;
    clearTimeout(debounce);
    debounce = setTimeout(() => { answer = null; runSearch({ ask: false }); }, SEARCH_DEBOUNCE_MS);
  });
  $('#fx-q').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); clearTimeout(debounce); runSearch({ ask: false }); }
    if (e.key === 'Escape') { e.preventDefault(); clearFilters(); }
  });
  $('#fx-ask').addEventListener('click', () => {
    if (!filters.q.trim()) { $('#fx-q').focus(); window.showToast?.('Type a question first', 'info'); return; }
    clearTimeout(debounce);
    runSearch({ ask: true });
  });
  $('#fx-clear').addEventListener('click', clearFilters);
  $('#fx-refresh').addEventListener('click', () => reload());
  $('#fx-upload')?.addEventListener('click', startUpload);

  container.querySelectorAll('[data-view]').forEach(btn => btn.addEventListener('click', () => {
    writeView(btn.dataset.view);
    draw();
  }));

  const bindFilter = (sel, key) => $(sel)?.addEventListener('change', (e) => { filters[key] = e.target.value; draw(); });
  bindFilter('#fx-f-type', 'docTypeId');
  bindFilter('#fx-f-asset', 'assetUUID');
  bindFilter('#fx-f-kind', 'kind');
  bindFilter('#fx-f-ai', 'ai');
  bindFilter('#fx-f-from', 'from');
  bindFilter('#fx-f-to', 'to');

  // The AI answer card is re-rendered on every draw, so its controls are
  // delegated. Its source links resolve against the FULL result set — a cited
  // document must open even when the current filters would hide its row, which
  // is why they carry their own attribute instead of the shared `data-file`.
  container.addEventListener('click', (e) => {
    if (e.target.closest('[data-role="answer-close"]')) { answer = null; draw(); return; }
    const src = e.target.closest('[data-src-file]');
    if (!src) return;
    const f = [...searchHits, ...allFiles].find(x => x.fileId === src.dataset.srcFile);
    if (f) openFileViewer(f, { docTypes: ctx.docTypes, assetName: assetNames.get(f.assetUUID) });
  });

  // ── Boot ─────────────────────────────────────────────────────────────────
  try { ctx.docTypes = (await listDocTypes(tenantId)) || []; } catch (e) { ctx.docTypes = []; }
  await loadAssetNames();
  await reload();
}

function shellHtml() {
  return `
    <div class="container-fluid py-3">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h1 class="h4 mb-0"><i class="fa-solid fa-folder-open me-2"></i>Files</h1>
        <div class="d-flex gap-2">
          <button id="fx-refresh" class="btn btn-sm btn-outline-secondary"><i class="fa-solid fa-rotate me-1"></i>Refresh</button>
          <button id="fx-upload" class="btn btn-sm btn-primary"><i class="fa-solid fa-upload me-1"></i>Upload</button>
        </div>
      </div>

      <div class="card shadow-sm mb-3">
        <div class="card-body py-3">
          <div class="input-group mb-2">
            <span class="input-group-text bg-white"><i class="fa-solid fa-magnifying-glass text-secondary"></i></span>
            <input id="fx-q" type="search" class="form-control" autocomplete="off"
                   placeholder="Search labels, file names, tags and document content…"
                   aria-label="Search files">
            <button id="fx-ask" class="btn btn-outline-primary" type="button"
                    title="Answer this as a question over the indexed documents">
              <i class="fa-solid fa-wand-magic-sparkles me-1"></i>Ask AI
            </button>
            <button id="fx-clear" class="btn btn-outline-secondary" type="button" title="Clear search and filters">
              <i class="fa-solid fa-eraser"></i>
            </button>
          </div>
          <div class="fx-filters">
            <select id="fx-f-type" class="form-select form-select-sm" aria-label="Document type"></select>
            <select id="fx-f-asset" class="form-select form-select-sm" aria-label="Asset"></select>
            <select id="fx-f-kind" class="form-select form-select-sm" aria-label="Kind">
              <option value="">Files and photos</option>
              <option value="file">Documents only</option>
              <option value="photo">Photos only</option>
            </select>
            <select id="fx-f-ai" class="form-select form-select-sm" aria-label="AI index">
              <option value="">Any indexing state</option>
              <option value="indexed">Has AI index</option>
              <option value="none">Not indexed</option>
            </select>
            <input id="fx-f-from" type="date" class="form-control form-control-sm" aria-label="Uploaded from" title="Uploaded from">
            <input id="fx-f-to" type="date" class="form-control form-control-sm" aria-label="Uploaded to" title="Uploaded to">
          </div>
        </div>
      </div>

      <div id="fx-answer"></div>

      <div class="fx-toolbar mb-2">
        <span id="fx-count" class="small text-muted"></span>
        <div class="btn-group btn-group-sm fx-viewtoggle ms-auto" role="group" aria-label="View mode">
          <button type="button" class="btn btn-outline-secondary" data-view="list" title="List"><i class="fa-solid fa-list"></i></button>
          <button type="button" class="btn btn-outline-secondary" data-view="tiles" title="Thumbnails"><i class="fa-solid fa-table-cells-large"></i></button>
        </div>
      </div>

      <div id="fx-body"><div class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading files…</div></div>
    </div>`;
}

/**
 * Searchable asset picker for the explorer's upload button (the uploader itself
 * is asset-bound). Resolves to an assetUUID or null.
 */
function pickAsset(assets) {
  return new Promise((resolve) => {
    const id = `fx-pick-${Math.random().toString(36).slice(2)}`;
    const sorted = [...assets].sort((a, b) => String(assetTitle(a)).localeCompare(String(assetTitle(b))));

    document.body.insertAdjacentHTML('beforeend', `
      <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header py-2"><h5 class="modal-title">Attach to which asset?</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>
            <div class="modal-body">
              <input id="${id}-q" class="form-control form-control-sm mb-2" type="search" autocomplete="off"
                     placeholder="Filter by name, reference or serial…" aria-label="Filter assets">
              <div id="${id}-list" class="list-group list-group-flush" style="max-height:52vh;overflow:auto;"></div>
            </div>
          </div>
        </div>
      </div>`);

    const el = document.getElementById(id);
    const modal = new bootstrap.Modal(el);
    const listEl = el.querySelector(`#${id}-list`);
    let picked = null;

    const render = (q) => {
      const needle = q.trim().toLowerCase();
      const rows = (needle
        ? sorted.filter(a => [a.label, a.reference, a.serial, a.assetId].some(v => String(v || '').toLowerCase().includes(needle)))
        : sorted).slice(0, 200);
      listEl.innerHTML = rows.length ? rows.map(a => `
        <button type="button" class="list-group-item list-group-item-action py-2" data-uuid="${escapeHtml(a.assetUUID)}">
          <div class="fw-semibold small">${escapeHtml(assetTitle(a))}</div>
          ${a.reference && a.reference !== assetTitle(a) ? `<div class="text-muted" style="font-size:.72rem;">${escapeHtml(a.reference)}</div>` : ''}
        </button>`).join('')
        : '<div class="text-muted small p-3 text-center">No asset matches.</div>';
    };

    render('');
    el.querySelector(`#${id}-q`).addEventListener('input', (e) => render(e.target.value));
    listEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-uuid]');
      if (!btn) return;
      picked = btn.dataset.uuid;
      modal.hide();
    });
    el.addEventListener('shown.bs.modal', () => el.querySelector(`#${id}-q`).focus(), { once: true });
    el.addEventListener('hidden.bs.modal', () => { el.remove(); resolve(picked); }, { once: true });
    modal.show();
  });
}
